import { Button, CardProps, Space } from "antd";
import React, { useState } from "react";
import {
  AccountingStatement,
  GetAccountingStatementsListParams,
  JOB_STATUS,
  ReconciliationGroup
} from "types/reconciliation";
import StatementTitlesTable from "../statementTitlesTable/StatementTitlesTable";
import { StatementTitlesTableProvider } from "../../../context/StatementTitlesTableContext";
import {
  AccountingStatementsTableProvider,
  useAccountingStatementsTable
} from "../../../context/AccountingStatementsTableContext";
import { AccountingStatementsTable } from "./AccountingStatementsTable";
import { NavigationCard, NavigationCardProps } from "../NavigationCard";
import { SyncOutlined } from "@ant-design/icons";
import { useGetAccountingStatementsListQuery } from "../../../redux/api/reconciliationsApiSlice";

export const RefreshAccountingStatementsButton = ({ defaultFilters }: {
  defaultFilters: GetAccountingStatementsListParams
}) => {

  const { getParamsForApiRequest } = useAccountingStatementsTable();

  const {
    refetch
  } = useGetAccountingStatementsListQuery(getParamsForApiRequest(defaultFilters));

  return (
    <Button
      type={"link"}
      icon={<SyncOutlined />}
      onClick={() => {
        refetch();
      }}>
      Aggiorna
    </Button>
  );
};

/**
 * ReconciliationsTable is a functional component responsible for rendering a table of accounting statements
 * (reconciliations) with various functionalities, such as sorting, pagination, expandable rows, and action buttons.
 *
 * The table displays columns including job status, broker, statement period, statement number, document name, and actions.
 * The data is fetched using the `useGetAccountingStatementsListQuery` hook and can be refreshed manually or automatically
 * at a polling interval of 120 seconds. The component also manages client-side state for pagination, page size, and column ordering.
 *
 * Features:
 * - Displays a customizable table with sorting on multiple columns.
 * - Supports pagination with customizable page size and total items.
 * - Allows dynamic updates of sorting order based on column interactions.
 * - Provides expandable rows to display related data for certain records.
 * - Includes action buttons for downloading reconciliation titles and deleting accounting statements, depending on record status.
 * - Refresh functionality to manually re-fetch the data.
 */
export const AccountingStatementsCard = ({
                                           relatedReconciliationGroup,
                                           onTableRowSelect,
                                           cardProps,
                                           navigationCardProps
                                         }: {
  relatedReconciliationGroup: ReconciliationGroup,
  onTableRowSelect?: (row: AccountingStatement) => void,
  cardProps?: CardProps
  navigationCardProps: (showsReconciliation: boolean) => NavigationCardProps
}) => {

  const [showReconciliation, setShowReconciliation] = useState(false);

  const showReconciliationButton = () => (
    <Button
      type={"link"}
      disabled={relatedReconciliationGroup.reconciliation_job_status === null || relatedReconciliationGroup.reconciliation_job_status === JOB_STATUS.PENDING}
      onClick={() => {
        setShowReconciliation(show => !show);
      }}>
      {showReconciliation ? "Nascondi riconciliazione" : "Mostra riconciliazione"}
    </Button>
  );

  return (
    <>
      {showReconciliation && (
        <NavigationCard
          {...navigationCardProps(showReconciliation)}
          cardProps={{
            extra: showReconciliationButton(),
            ...cardProps
          }}>
          <StatementTitlesTableProvider>
            <StatementTitlesTable
              showReconciliationColumns={true}
              defaultFilters={{ reconciliation_group: relatedReconciliationGroup.uuid, to_be_reconciled: true }}
              showConfirmedTitlesColumn={true}
            />
          </StatementTitlesTableProvider>
        </NavigationCard>
      )}
      {!showReconciliation && (
        <AccountingStatementsTableProvider>
          <NavigationCard
            {...navigationCardProps(showReconciliation)}
            cardProps={{
              extra: <Space direction={"horizontal"}>
                <RefreshAccountingStatementsButton
                  defaultFilters={{ reconciliation_group: relatedReconciliationGroup.uuid }} />
                {showReconciliationButton()}
              </Space>,
              ...cardProps
            }}
          >
            <AccountingStatementsTable
              defaultFilters={{ reconciliation_group: relatedReconciliationGroup.uuid }}
              onRecordDetailClick={onTableRowSelect} />
          </NavigationCard>
        </AccountingStatementsTableProvider>
      )}
    </>
  );

};
